import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { debounce } from '@ember/runloop';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import type { PopupListItem, Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';

import didInsert from 'webapp/modifiers/did-insert';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonNavigationCounterComponent from 'webapp/components/common/navigation-counter';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';

import type ReportPresentManager from 'webapp/models-custom/report-present-manager';

interface Args {
	model: ReportPresentManager;
}

type Action = 'openPresentWindow' | 'enterFullscreen' | 'exitFullscreen' | 'exitPresent';
type Option = PopupListItem<Action>;

export default class ReportsPresentHelperComponent extends Component<Args> {
	@service('popup') declare popupService: Service.Popup;

	node: HTMLElement | null = null;

	@tracked isOptionsOpen = false;
	@tracked isClose = false;

	get options() {
		const options: Option[] = [];

		const model = this.args.model;

		if (!model.isPresentWindowOpen) {
			options.push({
				icon: 'AlignLeft',
				text: 'Open notes',
				action: 'openPresentWindow',
			});
		}

		if (model.isFullscreen) {
			options.push({
				icon: 'Minimize',
				text: 'Exit full-screen',
				action: 'exitFullscreen',
			});
		} else {
			options.push({
				icon: 'Expand',
				text: 'Enter full-screen',
				action: 'enterFullscreen',
			});
		}

		options.push({
			class: 'close',
			text: 'Exit presentation',
			action: 'exitPresent',
			icon: 'Close',
		});

		return options;
	}

	get isVisible() {
		return this.isOptionsOpen || this.isClose;
	}

	get slideOptions() {
		return this.args.model.sortedSlides.map((slide, index) => ({
			index: index,
			text: slide.shownTitle || `Slide ${index + 1}`,
		}));
	}

	@action
	mouseMoveChecker(e: MouseEvent) {
		debounce(this, this.checkIfClose, e, 100);
	}

	private checkIfClose(e: MouseEvent) {
		const node = this.node;
		if (!node) {
			return;
		}
		const nodePos = node.getBoundingClientRect(),
			isClose = nodePos.top - 200 <= e.clientY;

		this.isClose = isClose;
	}

	@action
	openOptions(e: PointerEvent) {
		if (this.isOptionsOpen) {
			return;
		}

		const attachTo = e.target as HTMLElement;
		const popup = this.popupService.createListPopup(attachTo, undefined, 'board-options');

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: (actionName: string) => {
					this.doOptionsAction(actionName);
				},
			});

			this.isOptionsOpen = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.isOptionsOpen = false;
				}
			});
		}
	}

	@action
	goPrevious() {
		this.args.model.handleNavigation(true);
	}

	@action
	goNext() {
		this.args.model.handleNavigation();
	}

	@action
	doOptionsAction(actionName: string): void {
		this[actionName as Action]();
	}

	@action
	goToSlide(slideIndex: number) {
		this.args.model.changeSlideIndexIfExists(slideIndex);
	}

	@action
	openPresentWindow() {
		this.exitFullscreen();
		this.args.model.openPresentWindow();
	}

	@action
	enterFullscreen() {
		this.args.model.isFullscreen = true;
	}

	@action
	exitFullscreen() {
		this.args.model.isFullscreen = false;
	}

	@action
	exitPresent() {
		this.args.model.handleExit();
	}

	@action
	setupView(node: HTMLElement) {
		this.node = node;
		document.body.addEventListener('mousemove', this.mouseMoveChecker);
	}

	@action
	teardownView() {
		document.body.removeEventListener('mousemove', this.mouseMoveChecker);
	}
	<template>
		<div
			id="present-helper"
			class={{unless this.isVisible "hidden"}}
			{{didInsert this.setupView}}
			{{willDestroy this.teardownView}}
		>
			<a class="nav-btn" {{on "click" this.goPrevious}}>
				<CommonUiIconComponent @name="ChevronLeft" @size="sm" />
			</a>
			<CommonNavigationCounterComponent
				@items={{this.slideOptions}}
				@selectedIndex={{@model.currentSlideIndex}}
				@onSelect={{this.goToSlide}}
				@class="pos"
			/>
			<a class="nav-btn" {{on "click" this.goNext}}>
				<CommonUiIconComponent @name="ChevronRight" @size="sm" />
			</a>
			<a class="more {{if this.isOptionsOpen 'active'}}" {{on "click" this.openOptions}}>
				<CommonUiIconComponent @name="Ellipsis" @size="md" />
			</a>
		</div>
	</template>
}
